import { Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';

/**
 * Neutralises Chrome's autofill highlight on login inputs.
 *
 * Chrome paints its own background on `input:-webkit-autofill`, but the eye-icon
 * adornment sits in the outer OutlinedInput rather than the input itself, so an
 * autofilled password renders in two colours — tinted across the text, untinted
 * behind the icon. Overriding with an inset shadow in the surrounding colour is the
 * usual workaround: the background cannot be set directly, as Chrome overrides it.
 *
 * The long transition keeps the override in place; Chrome re-applies its colour on
 * focus changes, and a transition it never reaches holds the field steady.
 *
 * Applied to the email field too — Chrome fills credentials as a pair, so tinting
 * only one of them is the same mismatch one field higher.
 */
export const autofillSx = (theme: Theme): SystemStyleObject<Theme> => ({
  '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
    WebkitBoxShadow: `0 0 0 100px ${theme.palette.background.paper} inset`,
    WebkitTextFillColor: theme.palette.text.primary,
    caretColor: theme.palette.text.primary,
    transition: 'background-color 9999s ease-in-out 0s',
  },
});

/**
 * Corner radius shared by the two login switchers (CNA-1969).
 *
 * LanguageSwitcher and RegionSwitcher sit side by side under the card, so they read as one
 * pair of controls and a different corner on each reads as a mistake. Shared here rather
 * than written twice because that is the actual requirement — the two have to agree, and a
 * comment asking the next person to keep them in step is weaker than one value.
 *
 * A string, deliberately. A bare number in `sx.borderRadius` is a *multiplier* of
 * `theme.shape.borderRadius` — 4px here, MUI's default, which `common-ui/src/config/theme.ts`
 * does not override — so `10` would render at 40px. That multiplication is what had the
 * region switcher at 20px to begin with, from `borderRadius: 5`.
 *
 * Not to be confused with the theme's `spacing`, which *is* overridden, to 0.25rem per unit
 * (4px) rather than MUI's 8px. Same number, unrelated mechanism.
 */
export const SWITCHER_RADIUS = '10px';
